/**
 * Per-page document head, on the browser side.
 *
 * The server has already written the correct tags for this URL — see
 * src/server/html.ts. This component re-applies them after a client-side
 * navigation, when no new document is fetched and the head would otherwise
 * still describe the page the visitor came from.
 *
 * This UPDATES the tags already in the document rather than rendering new
 * ones.
 *
 * That distinction matters. React 19 will happily hoist a <meta> or <link>
 * rendered anywhere in the tree into <head>, and it is tempting to use it —
 * but it does not deduplicate them against what is already there. Rendering a
 * canonical here while the served HTML also carries one puts two
 * <link rel="canonical"> on every page, and Google's documented behaviour when
 * it finds more than one is to ignore all of them.
 *
 * The strings come from src/shared/seo.ts, which is also what the server
 * reads. Neither half owns the copy, so they cannot disagree about it.
 */
import { useEffect } from 'react';

import { useI18n } from '../i18n';
import { canonicalFor, isPagePath, ogImageFor, ogLocaleFor, seoFor } from '../../shared/seo';

interface SeoProps {
  /** Path without the locale prefix, e.g. '/privacy'. */
  path: string;
  /**
   * Only for a route that is not a real page — the 404, whose copy lives in
   * the dictionaries because it is rendered on screen as well as in the head.
   * Every real page leaves these unset and takes its strings from the table.
   */
  title?: string;
  description?: string;
  /** Keep a page out of search results — used by the 404. */
  noindex?: boolean;
}

/** Find the tag by its identifying attribute, or create it, then set content. */
function setMeta(attr: 'name' | 'property', key: string, content: string): void {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function Seo({
  path,
  title: titleOverride,
  description: descriptionOverride,
  noindex,
}: SeoProps) {
  const { locale } = useI18n();

  const page = seoFor(path, locale);
  const title = titleOverride ?? page?.title ?? '';
  const description = descriptionOverride ?? page?.description ?? '';

  /*
   * A path that is not a real page gets no canonical and no hreflang. A 404 is
   * not an alternate version of anything, and pointing its canonical at the
   * home page would invite Google to index the home page under this URL.
   *
   * Kept as strings rather than an array so the effect's dependencies stay
   * stable across renders.
   */
  const real = isPagePath(path);
  const canonical = real ? canonicalFor(path, locale) : null;
  const enHref = real ? canonicalFor(path, 'en') : null;
  const esHref = real ? canonicalFor(path, 'es') : null;

  const ogImage = ogImageFor(locale);

  useEffect(() => {
    document.title = title;

    setMeta('name', 'description', description);

    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:image', ogImage);
    setMeta('property', 'og:image:alt', title);
    setMeta('property', 'og:locale', ogLocaleFor(locale));

    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', ogImage);

    const existingCanonical = document.head.querySelector('link[rel="canonical"]');
    if (canonical) {
      setLink('canonical', canonical);
      setMeta('property', 'og:url', canonical);
    } else {
      existingCanonical?.remove();
      document.head.querySelector('meta[property="og:url"]')?.remove();
    }

    /*
     * hreflang tells Google these are the same page in two languages, so it
     * shows the Spanish one to Spanish speakers instead of treating them as
     * duplicates competing with each other. x-default names the version to
     * serve anyone the other two do not cover.
     *
     * Replaced as a set rather than updated one by one: there is no stable
     * single attribute to key each one on across a locale change.
     */
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());

    if (enHref && esHref) {
      for (const [hreflang, href] of [
        ['en', enHref],
        ['es', esHref],
        ['x-default', enHref],
      ] as const) {
        const el = document.createElement('link');
        el.setAttribute('rel', 'alternate');
        el.setAttribute('hreflang', hreflang);
        el.setAttribute('href', href);
        document.head.appendChild(el);
      }
    }

    // Added and removed rather than set to "index", because the absence of the
    // tag is the default and says the same thing more quietly.
    const robots = document.head.querySelector('meta[name="robots"]');
    if (noindex) {
      setMeta('name', 'robots', 'noindex, follow');
    } else if (robots) {
      robots.remove();
    }
  }, [title, description, canonical, enHref, esHref, ogImage, locale, noindex]);

  return null;
}
